import { DataResult, DataSourceRequestState } from '@progress/kendo-data-query';
import { endOfDay, sub } from 'date-fns';
import {
  MixtureAvgParameterDto,
  MixtureDto,
  MixtureSpecificationDto,
  MixtureStatusDto,
  MixtureStdPracticeDto
} from 'src/store/mixtures/mixture';
import { ArrayUtils } from 'src/utils/array';
import { ApiError } from '../api-handler';
import { MixtureAction, MixtureTypes } from './mixtures.actions';

export type MixturesState = Readonly<{
  mixtures: MixtureDto[];
  mixtureStatuses: MixtureStatusDto[];
  filteredMixtures: DataResult;
  mixtureAvgParameters: MixtureAvgParameterDto[];
  mixtureStdPractice: MixtureStdPracticeDto[];
  mixtureSpecification: MixtureSpecificationDto[];
  numberOfOngoingMixtures: number;
  errors: Nullable<ApiError>;
  requestState: DataSourceRequestState;
}>;

export const initialState: MixturesState = {
  mixtures: [],
  mixtureStatuses: [],
  filteredMixtures: { data: [], total: 0 },
  mixtureAvgParameters: [],
  mixtureStdPractice: [],
  mixtureSpecification: [],
  numberOfOngoingMixtures: 0,
  errors: null,
  requestState: {
    skip: 0,
    take: 20,
    sort: [{ field: 'no', dir: 'desc' }],
    filter: {
      logic: 'and',
      filters: [
        {
          field: 'mixDate',
          value: sub(new Date(), {
            months: 1
          }),
          operator: 'gte'
        },
        {
          field: 'mixDate',
          value: endOfDay(new Date()),
          operator: 'lte'
        }
      ]
    }
  }
};

const mixturesReducer = (
  state: MixturesState = initialState,
  action: MixtureAction
): MixturesState => {
  switch (action.type) {
    case MixtureTypes.FetchAll:
      return { ...state, mixtures: action.mixtures };
    case MixtureTypes.FetchFiltered:
      return { ...state, filteredMixtures: action.filteredMixtures };
    case MixtureTypes.FetchStatuses:
      return { ...state, mixtureStatuses: action.mixtureStatuses };
    case MixtureTypes.Delete:
      return {
        ...state,
        mixtures: ArrayUtils.deleteFromListById(state.mixtures, action.mixture),
        filteredMixtures: {
          ...state.filteredMixtures,
          data: ArrayUtils.deleteFromListById(
            state.filteredMixtures.data,
            action.mixture
          )
        }
      };
    case MixtureTypes.FetchMixtureAvgParameters:
      return { ...state, mixtureAvgParameters: action.mixtureAvgParameters };
    case MixtureTypes.FetchMixtureStdPractice:
      return { ...state, mixtureStdPractice: action.mixtureStdPractice };
    case MixtureTypes.FetchMixtureSpecifications:
      return { ...state, mixtureSpecification: action.mixtureSpecifications };
    case MixtureTypes.FetchNumberOfOngoingMixtures:
      return { ...state, numberOfOngoingMixtures: action.count };
    case MixtureTypes.Failure:
      return { ...state, errors: action.errors };
    case MixtureTypes.UpdateRequestState:
      return { ...state, requestState: action.requestState };
    default:
      return state;
  }
};

export default mixturesReducer;
